// Async Clipboard image writes work reliably with image/png across Chromium/Safari/WebKit.
// Raw JPEG/WebP blobs (or empty blob.type from XHR) often fail ClipboardItem / write().
async function blobToPngBlob(blob: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D unavailable");
    ctx.drawImage(bitmap, 0, 0);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((b) => {
        if (b) resolve(b);
        else reject(new Error("PNG encode failed"));
      }, "image/png");
    });
  } finally {
    bitmap.close();
  }
}

/**
 * iOS Safari keeps clipboard writes tied to the user gesture: awaiting fetch/canvas
 * before `clipboard.write` often yields NotAllowedError. Pass a `Promise<Blob>` into
 * `ClipboardItem` so encode/network work resolves inside the clipboard pipeline.
 */
export function writeImageBlobPromiseToClipboard(
  imageBlobPromise: Promise<Blob>,
): Promise<void> {
  if (!navigator.clipboard?.write) {
    return Promise.reject(new Error("Clipboard API not available"));
  }
  const pngPromise = imageBlobPromise.then((blob) =>
    blob.type === "image/png" ? blob : blobToPngBlob(blob),
  );
  return navigator.clipboard.write([
    new ClipboardItem({ "image/png": pngPromise }),
  ]);
}

/**
 * When the image is already a `Blob` in memory, this is fine. If the blob comes from
 * `fetch`/axios after an `await`, use {@link writeImageBlobPromiseToClipboard} instead
 * (e.g. wire `downloadImageProxy(...)` directly) so iOS Safari still sees a valid gesture.
 */
export function writeImageBlobToClipboard(imageBlob: Blob): Promise<void> {
  return writeImageBlobPromiseToClipboard(Promise.resolve(imageBlob));
}
